import { Card, Text } from '@tremor/react';
import type { User } from '../api/types';
import { t } from '../lib/i18n';

/**
 * Global (every-route) banner surfacing the /api/me `user.license` state so
 * users understand *why* write buttons are disabled instead of just hitting
 * a silent 403 (the Go server's Task 10 middleware is the real enforcement —
 * this is the UX layer). Mounted once in App.tsx via a thin self-fetching
 * container (mirrors CountermanKioskRedirect) since this app has no shared
 * user context/layout — this is the presentational half, prop-driven so it's
 * directly testable.
 *
 * Mirrors SeatBanner's LOOK (Tremor `<Card decoration="left">`, green/amber/
 * red tone) but reads a DIFFERENT data source: /api/me's `user.license`
 * (this task), not /api/admin/license's `Entitlement` (SeatBanner,
 * admin-only). Don't conflate the two.
 *
 * Three visual buckets:
 *  - `ok`, or `license` absent (old/mock session) → hidden (null). The
 *    overwhelming common case.
 *  - `grace` / `expired` → owner-only renewal banner (amber / red). Shown
 *    ONLY to the owner role — every other role's write buttons are already
 *    disabled via licenseAllowsWrites, and a "renew now" banner they can't
 *    act on would just be noise (or alarm staff needlessly).
 *  - `cannot_validate` / `misconfigured` / `clock_skew` → a distinct
 *    "cannot validate" family banner (amber), shown to ALL roles since it's
 *    a connectivity/config problem rather than a billing one — copy is
 *    deliberately NOT "vencida"/expired wording.
 */
export function LicenseBanner({ user }: { user: User | null | undefined }) {
  if (!user || !user.license) return null;
  const { status } = user.license;
  if (status === 'ok') return null;

  const toneClass = {
    amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200',
    red:   'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
  };

  if (status === 'grace' || status === 'expired') {
    // Non-owners never see the renewal banner — their write buttons are
    // already disabled (licenseAllowsWrites), and renewal isn't something
    // they can act on.
    if (user.role !== 'owner') return null;
    const message = status === 'grace' ? t.licenseBanner.grace : t.licenseBanner.expired;
    return (
      <Card decoration="left" className={status === 'grace' ? toneClass.amber : toneClass.red}>
        <Text className="font-semibold">{message}</Text>
      </Card>
    );
  }

  // cannot_validate / misconfigured / clock_skew — distinct "cannot
  // validate" family, shown to every role.
  const message =
    status === 'misconfigured' ? t.licenseBanner.misconfigured :
    status === 'clock_skew'    ? t.licenseBanner.clockSkew :
    t.licenseBanner.cannotValidate;
  return (
    <Card decoration="left" className={toneClass.amber}>
      <Text className="font-semibold">{message}</Text>
    </Card>
  );
}
